import { useState, FormEvent, ChangeEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminStatCards from './components/AdminStatCards';
import MembersTable from './components/MembersTable';

const ADMIN_EMAIL = 'aiconsultingauthority@gmail.com';
const STORAGE_KEY = 'fr_admin_auth';

/* ── Admin login gate ────────────────────────────────────────────── */
function AdminLoginGate({ onAuth }: { onAuth: () => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) { setError('Please enter your credentials.'); return; }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);

    if (form.email.toLowerCase() !== ADMIN_EMAIL) {
      setError('Access denied. This portal is restricted to authorized administrators only.');
      setTimeout(() => navigate('/'), 2000);
      return;
    }
    localStorage.setItem(STORAGE_KEY, 'true');
    onAuth();
  };

  return (
    <div className="min-h-screen bg-navy-dark flex items-center justify-center px-6 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(245,158,11,0.4) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />
      </div>

      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/4 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
              alt="Foreclosure Recovery"
              className="h-12 w-auto object-contain mx-auto mb-6"
            />
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 border border-gold/25 rounded-full mb-5">
            <i className="ri-shield-keyhole-line text-gold text-sm" />
            <span className="text-gold text-xs font-bold uppercase tracking-widest">Restricted Access</span>
          </div>
          <h1 className="font-playfair text-3xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-white/35 text-sm">Authorized personnel only</p>
        </div>

        <div className="bg-surface-2 border border-white/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>

            <div>
              <label className="block text-xs font-semibold text-white/40 uppercase tracking-widest mb-2">
                Admin Email
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-white/25 text-sm" />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="admin@example.com"
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold/60 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/40 uppercase tracking-widest mb-2">
                Password
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-white/25 text-sm" />
                <input
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-11 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold/60 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/60 cursor-pointer transition-colors"
                >
                  <i className={showPass ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-xs leading-relaxed">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-4 bg-gold text-navy-dark font-bold text-base rounded-xl hover:bg-gold-light transition-all duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer mt-1"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg" />
                  Verifying…
                </>
              ) : (
                <>
                  <i className="ri-shield-check-line text-lg" />
                  Access Admin Panel
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-white/20 text-xs mt-6">
          Unauthorized access attempts are logged.{' '}
          <Link to="/" className="text-white/35 hover:text-white/60 transition-colors underline">
            Return to home
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ── Admin dashboard ─────────────────────────────────────────────── */
function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'tickets' | 'revenue'>('overview');

  return (
    <div className="min-h-screen bg-navy-dark font-sans">

      {/* ── Top navigation bar ── */}
      <header className="sticky top-0 z-30 flex items-center justify-between px-6 lg:px-10 py-0 border-b border-white/8 bg-navy-dark/95 backdrop-blur-sm" style={{ height: '64px' }}>
        <div className="flex items-center gap-4">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
              alt="Foreclosure Recovery"
              className="h-8 w-auto object-contain"
            />
          </Link>
          <div className="w-px h-6 bg-white/10" />
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shield-star-line text-gold text-base" />
            </div>
            <span className="text-white/60 text-sm font-semibold">Admin Panel</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <button className="relative w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 text-white/40 hover:text-white hover:bg-white/8 transition-colors cursor-pointer">
            <i className="ri-notification-3-line text-base" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
          </button>

          {/* Admin avatar */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold/20 border border-gold/30 text-gold text-xs font-bold flex-shrink-0">
              JL
            </div>
            <div className="hidden sm:block">
              <p className="text-white text-sm font-semibold leading-tight">Jeffery Long</p>
              <p className="text-white/35 text-xs leading-tight">Super Admin</p>
            </div>
          </div>

          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white/50 text-sm font-semibold rounded-xl hover:border-red-500/30 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line text-base" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* ── Page content ── */}
      <main className="px-6 lg:px-10 py-8 max-w-screen-2xl mx-auto">

        {/* Page heading */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-xs font-semibold uppercase tracking-widest">Live</span>
            </div>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-white">Admin Panel</h1>
            <p className="text-white/35 text-sm mt-1">
              Welcome back, <strong className="text-white/60">Jeffery Long</strong> — here&apos;s your platform overview
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2.5 bg-gold/10 border border-gold/25 text-gold text-sm font-semibold rounded-xl hover:bg-gold/15 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-download-2-line" />
              Export Report
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-gold text-navy-dark text-sm font-bold rounded-xl hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-user-add-line" />
              Add Member
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <AdminStatCards />

        {/* ── Tab navigation ── */}
        <div className="flex items-center gap-1 mt-10 mb-6 bg-surface-2 border border-white/8 rounded-xl p-1 w-fit">
          {([
            { key: 'overview', icon: 'ri-layout-grid-line', label: 'Overview' },
            { key: 'members',  icon: 'ri-group-line',       label: 'Members' },
            { key: 'tickets',  icon: 'ri-customer-service-2-line', label: 'Tickets' },
            { key: 'revenue',  icon: 'ri-bar-chart-2-line', label: 'Revenue' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-gold text-navy-dark'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              <i className={`${tab.icon} text-base`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'members'  && <MembersTable />}
        {activeTab === 'tickets'  && <TicketsTab />}
        {activeTab === 'revenue'  && <RevenueTab />}
      </main>
    </div>
  );
}

/* ── Overview quick-stats grid ───────────────────────────────────── */
function OverviewTab() {
  return (
    <div className="flex flex-col gap-6">
      {/* Members table preview */}
      <MembersTable />

      {/* Two-column widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Recent signups */}
        <div className="bg-surface-2 border border-white/8 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-base">Recent Signups</h3>
            <span className="text-white/25 text-xs">Last 7 days</span>
          </div>
          <div className="flex flex-col gap-3">
            {[
              { name: 'Tanya Morrison', plan: 'Starter', time: '2 hours ago', state: 'FL' },
              { name: 'Derek Flemming', plan: 'Starter', time: 'Yesterday', state: 'TX' },
              { name: 'Sandra Whitfield', plan: 'Pro', time: '2 days ago', state: 'AZ' },
              { name: 'Kevin Torres', plan: 'Starter', time: '3 days ago', state: 'NV' },
              { name: 'Marcus Delgado', plan: 'Elite', time: '5 days ago', state: 'CA' },
            ].map((m, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-white/4 last:border-0">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold/12 border border-gold/20 text-gold text-xs font-bold flex-shrink-0">
                  {m.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{m.name}</p>
                  <p className="text-white/30 text-xs">{m.time} · {m.state}</p>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                  m.plan === 'Elite' ? 'bg-amber-500/15 text-amber-300 border-amber-400/30' :
                  m.plan === 'Pro' ? 'bg-gold/10 text-gold border-gold/25' :
                  'bg-white/5 text-white/50 border-white/10'
                }`}>
                  {m.plan}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Plan distribution */}
        <div className="bg-surface-2 border border-white/8 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-base">Plan Distribution</h3>
            <span className="text-white/25 text-xs">All time</span>
          </div>
          <div className="flex flex-col gap-4">
            {[
              { plan: 'Elite', count: 312, pct: 33, color: 'bg-amber-400' },
              { plan: 'Pro', count: 451, pct: 48, color: 'bg-gold' },
              { plan: 'Starter', count: 184, pct: 19, color: 'bg-white/20' },
            ].map((item) => (
              <div key={item.plan}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/70 text-sm font-semibold">{item.plan}</span>
                  <span className="text-white/40 text-xs">{item.count} members · {item.pct}%</span>
                </div>
                <div className="h-2 bg-white/6 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${item.color} transition-all duration-700`}
                    style={{ width: `${item.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-white/6 grid grid-cols-3 gap-4">
            {[
              { label: 'Avg. LTV', value: '$1,840' },
              { label: 'Churn Rate', value: '2.1%' },
              { label: 'MRR Growth', value: '+18.4%' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-playfair text-xl font-bold text-gold">{s.value}</p>
                <p className="text-white/30 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tickets placeholder tab ─────────────────────────────────────── */
function TicketsTab() {
  const tickets = [
    { id: 'TKT-001', user: 'Kevin Torres', subject: 'Cannot access property map', priority: 'High', status: 'Open', created: 'Mar 31, 2026' },
    { id: 'TKT-002', user: 'Sandra Whitfield', subject: 'Billing discrepancy on Pro plan', priority: 'High', status: 'Open', created: 'Mar 30, 2026' },
    { id: 'TKT-003', user: 'Diane Holloway', subject: 'Need to update email address', priority: 'Low', status: 'In Progress', created: 'Mar 29, 2026' },
    { id: 'TKT-004', user: 'Greg Hoffman', subject: 'Export feature not working', priority: 'Medium', status: 'Open', created: 'Mar 28, 2026' },
    { id: 'TKT-005', user: 'Lisa Calloway', subject: 'Password reset link expired', priority: 'Medium', status: 'In Progress', created: 'Mar 27, 2026' },
  ];

  const PRIORITY: Record<string, string> = {
    High:   'bg-red-500/12 text-red-400 border-red-500/20',
    Medium: 'bg-amber-500/12 text-amber-400 border-amber-500/20',
    Low:    'bg-white/5 text-white/40 border-white/10',
  };
  const STATUS_T: Record<string, string> = {
    Open:        'bg-gold/10 text-gold border-gold/25',
    'In Progress': 'bg-blue-500/10 text-blue-400 border-blue-400/25',
    Resolved:    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  };

  return (
    <div className="bg-surface-2 border border-white/8 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
        <div>
          <h2 className="text-white font-semibold text-base">Support Tickets</h2>
          <p className="text-white/35 text-xs mt-0.5">23 open tickets</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/25 text-gold text-sm font-semibold rounded-xl hover:bg-gold/15 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-filter-line" />
          Filter
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-white/5">
              {['Ticket ID', 'User', 'Subject', 'Priority', 'Status', 'Created', 'Action'].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-widest whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/4">
            {tickets.map((t) => (
              <tr key={t.id} className="hover:bg-white/3 transition-colors group">
                <td className="px-6 py-4 text-gold text-sm font-semibold whitespace-nowrap">{t.id}</td>
                <td className="px-6 py-4 text-white text-sm whitespace-nowrap">{t.user}</td>
                <td className="px-6 py-4 text-white/60 text-sm max-w-[220px] truncate">{t.subject}</td>
                <td className="px-6 py-4">
                  <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border whitespace-nowrap ${PRIORITY[t.priority]}`}>{t.priority}</span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border whitespace-nowrap ${STATUS_T[t.status]}`}>{t.status}</span>
                </td>
                <td className="px-6 py-4 text-white/40 text-sm whitespace-nowrap">{t.created}</td>
                <td className="px-6 py-4">
                  <button className="flex items-center gap-1.5 text-xs text-white/40 hover:text-gold transition-colors cursor-pointer whitespace-nowrap opacity-0 group-hover:opacity-100">
                    <i className="ri-reply-line" />
                    Reply
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Revenue placeholder tab ─────────────────────────────────────── */
function RevenueTab() {
  const months = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
  const values = [58400, 63100, 71200, 74900, 71800, 84920];
  const maxVal = Math.max(...values);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {[
          { label: 'Monthly Recurring Revenue', value: '$84,920', change: '+18.4%', icon: 'ri-money-dollar-circle-line' },
          { label: 'Annual Run Rate', value: '$1.02M', change: '+22.1%', icon: 'ri-line-chart-line' },
          { label: 'Avg Revenue / Member', value: '$89.67', change: '+4.3%', icon: 'ri-user-star-line' },
        ].map((s) => (
          <div key={s.label} className="bg-surface-2 border border-white/8 rounded-xl p-5 flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gold/10 border border-gold/20 flex-shrink-0">
              <i className={`${s.icon} text-gold text-xl`} />
            </div>
            <div>
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">{s.label}</p>
              <p className="font-playfair text-2xl font-bold text-white mt-0.5">{s.value}</p>
              <p className="text-emerald-400 text-xs font-semibold mt-0.5">{s.change} vs last month</p>
            </div>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      <div className="bg-surface-2 border border-white/8 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-semibold text-base">Revenue Trend (6 months)</h3>
          <span className="text-emerald-400 text-sm font-semibold flex items-center gap-1">
            <i className="ri-arrow-up-line" />
            45.4% over period
          </span>
        </div>
        <div className="flex items-end gap-4 h-48">
          {months.map((month, i) => {
            const heightPct = (values[i] / maxVal) * 100;
            const isLast = i === months.length - 1;
            return (
              <div key={month} className="flex-1 flex flex-col items-center gap-2">
                <p className="text-white/40 text-xs font-semibold">
                  ${(values[i] / 1000).toFixed(0)}K
                </p>
                <div className="w-full flex items-end justify-center" style={{ height: '160px' }}>
                  <div
                    className={`w-full rounded-t-lg transition-all duration-700 ${isLast ? 'bg-gold' : 'bg-white/10 hover:bg-white/15'}`}
                    style={{ height: `${heightPct}%` }}
                  />
                </div>
                <p className={`text-xs font-semibold ${isLast ? 'text-gold' : 'text-white/35'}`}>{month}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Root admin page ─────────────────────────────────────────────── */
export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === 'true') {
      setIsAuthed(true);
    }
  }, []);

  const handleAuth = () => setIsAuthed(true);

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setIsAuthed(false);
    navigate('/');
  };

  if (!isAuthed) {
    return <AdminLoginGate onAuth={handleAuth} />;
  }

  return <AdminDashboard onLogout={handleLogout} />;
}
